import { useEffect, useMemo, useState } from "react";
import type { LeadFormProps } from "../types/formTypes";
import {
  DAYS_FR,
  DAYS_FR_FULL,
  MONTHS_FR,
  addHoursToTime,
} from "../utils/leadFormUtils";

export type Step = "date" | "time" | "tradein" | "info" | "services" | "done";
export type YesNo = "yes" | "no" | null;

type Params = Pick<
  LeadFormProps,
  | "scrollToBottom"
  | "clearAttachedCar"
  | "formActive"
  | "onBooked"
  | "setFormActive"
  | "refreshSession"
>;

export function useLeadFormState({
  formActive,
  onBooked,
  scrollToBottom,
  clearAttachedCar,
  setFormActive,
  refreshSession,
}: Params) {
  const isTestDrive = formActive?.name === "lead_test_drive";
  const isTradeInLead = formActive?.name === "lead_garage_trade_in";

  // ✅ si isTestDrive mais pas de schedules => on NE fait PAS le flow test-drive
  const schedules = isTestDrive ? formActive?.schedules : undefined;
  const hasSchedules =
    !!schedules?.opening_days?.length && !!schedules?.schedule_slots?.length;

  const [overrideTestDriveFlow, setOverrideTestDriveFlow] = useState(false);

  const isTestDriveFlow = !overrideTestDriveFlow && isTestDrive && hasSchedules;

  const services = formActive?.services ?? [];

  // (questions services seulement si flow test-drive actif)
  const needsFinancingQuestion =
    isTestDriveFlow && services.includes("Financement");
  const needsTradeInQuestion =
    isTestDriveFlow && services.includes("Reprise de véhicules");
  const hasServiceQuestions = needsFinancingQuestion || needsTradeInQuestion;

  const steps = useMemo(() => {
    const arr: Exclude<Step, "done">[] = [];

    // lead_garage_trade_in: 1ère étape = reprise, dernière = coordonnées
    if (isTradeInLead) {
      arr.push("tradein", "info");
      return arr;
    }

    // lead_test_drive (avec schedules): flow complet
    if (isTestDriveFlow) arr.push("date", "time");

    // base: coordonnées (et éventuellement questions services)
    arr.push("info");
    if (hasServiceQuestions) arr.push("services");
    return arr;
  }, [isTradeInLead, isTestDriveFlow, hasServiceQuestions]);

  const [step, setStep] = useState<Step>(() => {
    if (isTradeInLead) return "tradein";
    return isTestDriveFlow ? "date" : "info";
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  // trade-in fields
  const [plateNumber, setPlateNumber] = useState("");
  const [mileage, setMileage] = useState("");

  const [hasCtDefects, setHasCtDefects] = useState<YesNo>(null);
  const [isFirstOwner, setIsFirstOwner] = useState<YesNo>(null);
  const [isImported, setIsImported] = useState<YesNo>(null);
  const [isMaintenanceUpToDate, setIsMaintenanceUpToDate] =
    useState<YesNo>(null);

  const [tradeInMode, setTradeInMode] = useState<"cash" | "agency" | null>(
    null,
  );
  const [isAlreadyOnline, setIsAlreadyOnline] = useState<YesNo>(null);

  // used to avoid showing the CTA again after sending the estimation (test-drive optional flow)
  const [tradeInSent, setTradeInSent] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");

  const [wantsFinancing, setWantsFinancing] = useState<YesNo>(null);
  const [hasTradeIn, setHasTradeIn] = useState<YesNo>(null);

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const { firstDay, daysInMonth } = useMemo(
    () => getDaysInMonth(currentMonth),
    [currentMonth],
  );

  const isDateSelectable = (day: number) => {
    if (!isTestDriveFlow || !schedules) return false;

    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );

    if (date < today) return false;

    const dayName = DAYS_FR_FULL[date.getDay()];
    return schedules.opening_days.includes(dayName);
  };

  const formatDate = (date: Date) =>
    `${DAYS_FR[date.getDay()]} ${date.getDate()} ${MONTHS_FR[date.getMonth()]}`;

  const formatSlot2h = (start: string) =>
    `${start} - ${addHoursToTime(start, 2)}`;

  // trade-in validation
  const isTradeInValid =
    !!plateNumber.trim() &&
    !!mileage.trim() &&
    !!hasCtDefects &&
    !!isFirstOwner &&
    !!isImported &&
    !!isMaintenanceUpToDate &&
    !!tradeInMode &&
    !!isAlreadyOnline;

  // DONE screen CTA (test-drive flow only)
  const baseShowEstimateTradeInButton =
    isTestDriveFlow &&
    !tradeInSent &&
    // If the user explicitly said they have no trade-in vehicle, don't show the CTA
    !(needsTradeInQuestion && hasTradeIn === "no");

  const [showEstimateTradeInButton, setShowEstimateTradeInButton] = useState(
    baseShowEstimateTradeInButton,
  );

  const goToTradeInStep = () => {
    // When entering trade-in from test-drive context,
    // we disable test-drive flow logic
    setFormActive({
      name: "lead_garage_trade_in",
      label: "Estimation de votre véhicule",
    });
    setOverrideTestDriveFlow(true);
    setSelectedDate(null);
    setSelectedTime(null);
    setStep("tradein");
  };

  // Back from trade-in step: only meaningful for lead_garage_trade_in
  const canBackFromTradeIn = isTradeInLead;
  const goBackFromTradeIn = () => {
    // if we add a back button later, it should go back to trade-in step
    setStep("tradein");
  };

  // Back to time from info: only for test-drive flow
  const canBackToTimeFromInfo = isTestDriveFlow;

  const submitTradeInEstimation = () => {
    if (!isTradeInValid) return;

    // ✅ OPTION 1: we send a SECOND message/lead to backend
    const payload: any = {
      action: "trade_in_estimation",
      plateNumber: plateNumber.trim(),
      mileage: mileage.trim(),
      hasCtDefects: hasCtDefects === "yes",
      isFirstOwner: isFirstOwner === "yes",
      isImported: isImported === "yes",
      isMaintenanceUpToDate: isMaintenanceUpToDate === "yes",
      tradeInMode,
      isAlreadyOnline: isAlreadyOnline === "yes",
    };

    // include contact if already known (useful when coming from test-drive)
    if (name.trim()) payload.name = name.trim();
    if (phone.trim()) payload.phone = phone.trim();
    if (email.trim()) payload.email = email.trim();

    // include test-drive context if available
    if (selectedDate && selectedTime) {
      payload.date = selectedDate.toISOString().split("T")[0];
      payload.time = selectedTime;
    }

    onBooked(payload);
    setTradeInSent(true);
  };

  const goToNextAfterTradeIn = () => {
    if (!isTradeInValid) return;

    // lead_garage_trade_in: trade-in step first, then coordinates
    if (isTradeInLead) {
      setStep("info");
      return;
    }

    // lead_test_drive: estimation is optional after DONE
    submitTradeInEstimation();
    setStep("done");
  };

  const availableSlots = useMemo(() => {
    // ✅ aucun fallback : si pas de schedules => on ne passe jamais par le step time
    if (!isTestDriveFlow || !schedules?.schedule_slots?.length) return [];

    if (!selectedDate) return [];

    const dayName = DAYS_FR_FULL[selectedDate.getDay()];
    const dayConfig = schedules.schedule_slots.find(
      (d: any) => d.day === dayName,
    );

    return dayConfig?.slots ?? [];
  }, [isTestDriveFlow, schedules, selectedDate]);

  const goToNextAfterInfo = () => {
    // lead_garage_trade_in: last step is coordinates
    if (isTradeInLead) {
      handleSubmit();
      return;
    }

    if (hasServiceQuestions) {
      setStep("services");
      return;
    }

    handleSubmit();
  };

  const handleSubmit = () => {
    if (!name.trim() || !phone.trim() || !email.trim()) return;

    if (isTestDriveFlow) {
      if (!selectedDate || !selectedTime) return;
    }

    if (hasServiceQuestions) {
      if (needsFinancingQuestion && !wantsFinancing) return;
      if (needsTradeInQuestion && !hasTradeIn) return;
    }

    if (isTradeInLead) {
      if (!isTradeInValid) return;
    }

    const payload: Parameters<LeadFormProps["onBooked"]>[0] = {
      name: name.trim(),
      phone: phone.trim(),
      email: email.trim(),
    };

    if (isTestDriveFlow && selectedDate && selectedTime) {
      payload.date = selectedDate.toISOString().split("T")[0];
      payload.time = selectedTime;
    }

    if (needsFinancingQuestion)
      payload.wantsFinancing = wantsFinancing === "yes";
    if (needsTradeInQuestion) payload.hasTradeIn = hasTradeIn === "yes";

    if (isTradeInLead) {
      (payload as any).plateNumber = plateNumber.trim();
      (payload as any).mileage = mileage.trim();
      (payload as any).hasCtDefects = hasCtDefects === "yes";
      (payload as any).isFirstOwner = isFirstOwner === "yes";
      (payload as any).isImported = isImported === "yes";
      (payload as any).isMaintenanceUpToDate = isMaintenanceUpToDate === "yes";
      (payload as any).tradeInMode = tradeInMode;
      (payload as any).isAlreadyOnline = isAlreadyOnline === "yes";
    }

    onBooked(payload);
    setStep("done");
  };

  const prevMonth = () => {
    const prev = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth() - 1,
      1,
    );
    if (prev >= new Date(today.getFullYear(), today.getMonth(), 1)) {
      setCurrentMonth(prev);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  const stepLabel = (s: Exclude<Step, "done">) => {
    if (s === "date") return "Choisir une date";
    if (s === "time") return "Choisir un horaire";
    if (s === "tradein") return "Estimer votre reprise";
    if (s === "services") return "Quelques questions";
    return "Vos coordonnées";
  };

  const onSelectDate = (date: Date) => {
    setSelectedDate(date);
    setSelectedTime(null);
    setStep("time");
  };

  const onSelectTime = (t: string) => {
    setSelectedTime(t);
    setStep("info");
  };

  useEffect(() => {
    if (step !== "tradein") scrollToBottom();
  }, [step, scrollToBottom]);

  useEffect(() => {
    setShowEstimateTradeInButton(baseShowEstimateTradeInButton);
  }, [baseShowEstimateTradeInButton]);

  // Auto-clear attached car 5s after completion
  useEffect(() => {
    if (step !== "done") return;

    // Do NOT auto-clear if we show the trade-in estimation CTA
    if (showEstimateTradeInButton) return;

    const timeout = setTimeout(() => {
      clearAttachedCar?.();
      refreshSession();
    }, 5000);

    return () => clearTimeout(timeout);
  }, [step, showEstimateTradeInButton, clearAttachedCar, refreshSession]);

  return {
    // flags
    isTestDriveFlow,

    // steps
    steps,
    step,
    setStep,
    stepLabel,

    // date/time
    currentMonth,
    firstDay,
    daysInMonth,
    prevMonth,
    nextMonth,
    isDateSelectable,
    selectedDate,
    selectedTime,
    availableSlots,
    onSelectDate,
    onSelectTime,

    // formatting
    formatDate,
    formatSlot2h,

    // form
    name,
    phone,
    email,
    setName,
    setPhone,
    setEmail,

    // services questions
    needsFinancingQuestion,
    needsTradeInQuestion,
    hasServiceQuestions,
    wantsFinancing,
    hasTradeIn,
    setWantsFinancing,
    setHasTradeIn,

    // submit
    goToNextAfterInfo,
    handleSubmit,

    // trade-in
    plateNumber,
    mileage,
    hasCtDefects,
    isFirstOwner,
    isImported,
    isMaintenanceUpToDate,
    tradeInMode,
    isAlreadyOnline,
    setPlateNumber,
    setMileage,
    setHasCtDefects,
    setIsFirstOwner,
    setIsImported,
    setIsMaintenanceUpToDate,
    setTradeInMode,
    setIsAlreadyOnline,
    showEstimateTradeInButton,
    goToTradeInStep,
    goToNextAfterTradeIn,
    canBackFromTradeIn,
    goBackFromTradeIn,
    canBackToTimeFromInfo,
  };
}
